import {Observable} from 'rxjs';
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {AbstractService} from '../abstract.service';

import {TiposModel} from '../../model/planilhas/tipos.model';
import {Mensagem} from '../../model/planilhas/mensagem.model';
import {FormularioModel} from '../../model/planilhas/formulario.model';
import {ProcessoModel} from '../../model/planilhas/processo.model';
import {CampoFormularioModel} from '../../model/planilhas/campo-formulario.model';
import {ResponseDto} from '../../dto/response-dto';
import {Store} from '../../model/store';
import {switchMap, take, tap} from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class FormularioService extends AbstractService {

    private readonly URL = this.API_REST_PLANILHAS + "/formularios";

    private formularios: Store<any> = new Store(null);

    constructor(http: HttpClient) {
        super(http);
    }

    inativaAtiva(ativa: boolean, form_id: number): Observable<Mensagem> {
        return this.http.post<Mensagem>(`${this.URL}/inativa-ativa`, {ativa, form_id});
    }

    getAllFormularios(): Observable<FormularioModel[]> {
        return this.http.get<FormularioModel[]>(`${this.URL}`);
    }

    getFormulariosAtivosByFase(id: string | number): Observable<FormularioModel[]> {
        return this.http.get<FormularioModel[]>(`${this.URL}/fase/ativos/` + id);
    }

    getFormulariosByFase(id: string | number): Observable<FormularioModel[]> {
        return this.http.get<FormularioModel[]>(`${this.URL}/fase/` + id);
    }

    getFormulariosFiltroFaseAtivo(ids: number[], ativo: string): Observable<ResponseDto<FormularioModel[]>> {
        return this.http.get<ResponseDto<FormularioModel[]>>(`${this.URL}/fase/` + ids + '/ativo/' + ativo);
    }

    getFormulariosFiltroFaseAtivoCached(ids: string | number, ativo: string): Observable<any> {
        return this.formularios.state$.pipe(
            switchMap((formulario) => {
                if (!formulario || formulario.ids !== ids || formulario.ativo !== ativo) {
                    return this.http
                        .get<any>(
                            `${this.URL}/fase/` + ids + '/ativo/' + ativo
                        )
                        .pipe(
                            tap((result) =>
                                this.formularios.setState({
                                    ...result,
                                    ids,
                                    ativo
                                })
                            )
                        );
                }
                return this.formularios.state$;
            }),
            take(1)
        );
    }

    getAllRascunhos(): Observable<FormularioModel[]> {
        return this.http.get<FormularioModel[]>(`${this.URL}/all-rascunhos`);
    }

    getRascunhosByFaseId(id: number) {
        return this.http.get<ResponseDto<FormularioModel[]>>(`${this.URL}/rascunhos/fase/` + id);
    }

    getCampoFormulario(id?: number) {
        return this.http.get<ResponseDto<CampoFormularioModel>>(`${this.API_REST_PLANILHAS}/campo-formulario/` + id);
    }

    getCampoFormularioList(idList: number[]) {
        return this.http.get<ResponseDto<CampoFormularioModel[]>>(`${this.API_REST_PLANILHAS}/campo-formulario/list-id/` + idList.join());
    }

    getFormulariosFechadosByIdFase(id: number): Observable<FormularioModel[]> {
        return this.http.get<FormularioModel[]>(`${this.URL}/fechados/com-preenchimento/by/id-fase/` + id);
    }

    getFormulario(id?: number) {
        return this.http.get<ResponseDto<FormularioModel>>(`${this.URL}/` + id);
    }

    postFormulario(rascunho: any, form: FormularioModel) {
        const payload = {
            rascunho: rascunho,
            form: {
                ...form,
                rascunho: JSON.stringify(rascunho)
            },
        };
        return this.http.post<ResponseDto>(`${this.URL}`, payload);
    }

    putFormulario(form: FormularioModel): Observable<Mensagem> {
        return this.http.put<Mensagem>(`${this.URL}/`, form);
    }

    getTiposCamposFormulario(): Observable<TiposModel[]> {
        return this.http.get<TiposModel[]>(`${this.URL}/tipos_campos`);
    }

    getProcessosTodas(): Observable<ProcessoModel[]> {
        return this.http.get<ProcessoModel[]>(`${this.URL}/fase`);
    }

    getProcessosPreenchimento(id: number): Observable<ProcessoModel> {
        return this.http.get<ProcessoModel>(`${this.URL}/fase/preenchimento/` + id);
    }

    getFormulariosComPreenchimentos(id: string | number): Observable<FormularioModel[]> {
        return this.http.get<FormularioModel[]>(`${this.URL}/fase/` + id + `/com-preenchimentos/`);
    }

    ativarPlanilhaPorFase(idFormulario): Observable<Mensagem> {
        return this.http.post<Mensagem>(`${this.URL}/fase/ativar-planilha`, {idFormulario});
    }

    getRowsForms(id: number): Observable<number> {
        return this.http.get<number>(`${this.URL}/get/rows/forms/` + id);
    }

    salvaRascunho(rascunho: any[], form: FormularioModel) {
        const payload = {
            rascunho: rascunho,
            form: {
                ...form,
                rascunho: JSON.stringify(rascunho)
            },
        };
        return this.http.post<ResponseDto<FormularioModel>>(`${this.URL}/salva-rascunho`, payload);
    }

    updateRascunho(rascunho: any[], form: FormularioModel) {
        const payload = {
            rascunho: rascunho,
            form: {
                ...form,
                rascunho: JSON.stringify(rascunho)
            },
        };
        return this.http.post<ResponseDto<FormularioModel>>(`${this.URL}/update-rascunho`, payload);
    }

    finalizarFormulario(rascunho: any[], form: FormularioModel) {
        const payload = {
            rascunho: rascunho,
            form: {
                ...form,
                rascunho: JSON.stringify(rascunho)
            },
        };
        return this.http.post<ResponseDto>(`${this.URL}/finaliza-formulario`, payload);
    }

    getRascunhoById(id: number) {
        return this.http.get<ResponseDto<FormularioModel>>(`${this.URL}/rascunho/` + id);
    }

    importForms(listImport: FormularioModel[]) {
        const payload = [];
        const aux = JSON.parse(JSON.stringify(listImport));
        aux.forEach(form => {
            delete form['name'];
            const arrRascunho = form.rascunho;
            payload.push({
                form: form,
                rascunho: arrRascunho,
                metadados: form.metadados
            });
        });

        return this.http.post<ResponseDto>(`${this.URL}/import-forms`, JSON.stringify(payload));
    }
}
